import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Mail, Send, X, Eye, AlertTriangle, CheckCircle2, Paperclip,
  AlertCircle, Edit3, ShieldCheck,
} from "lucide-react";
import DOMPurify from "dompurify";

const inputCls = "w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-blue-500";

/**
 * Reusable confirmation-first email dialog.
 * Renders a template + entity context, lets the user inspect & edit subject/body/recipients,
 * then sends ONLY after explicit confirmation.
 *
 * Props:
 *   open                   — bool
 *   onClose                — () => void
 *   eventTrigger           — string (e.g. "invoice_send", "anmeldung")
 *   templateId             — optional, pin a specific template
 *   context                — { invoice_id?, employee_id?, customer_id?, vehicle_id?, contract? }
 *   relatedEntityType      — for audit log (e.g. "invoice")
 *   relatedEntityId        — for audit log
 *   onSent                 — () => void (after successful send)
 *   title                  — optional dialog title
 */
export default function SendEmailDialog({
  open, onClose, eventTrigger, templateId, context = {},
  relatedEntityType, relatedEntityId, onSent, title = "E-Mail versenden",
}) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("review");      // 'review' | 'preview' | 'success' | 'failed'
  const [result, setResult] = useState(null);

  // Form fields
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachmentIds, setAttachmentIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    setDraft(null); setError(""); setStage("review"); setResult(null);
    setLoading(true);
    api.post("/vorlagen/render", {
      template_id: templateId || undefined,
      event_trigger: eventTrigger || undefined,
      context,
    }).then(({ data }) => {
      setDraft(data);
      setTo(data.suggested_to || []);
      setCc(data.suggested_cc || []);
      setBcc(data.suggested_bcc || []);
      setSubject(data.subject || "");
      setBody(data.body || "");
      setAttachmentIds((data.attachments || []).map((a) => a.id));
    }).catch((e) => {
      setError(e.response?.data?.detail || "Vorlage nicht gefunden");
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventTrigger, templateId]);

  if (!open) return null;

  async function confirmSend() {
    if (!to.length) { toast.error("Mindestens ein Empfänger erforderlich"); return; }
    if (!subject.trim()) { toast.error("Betreff erforderlich"); return; }
    if (!body.trim()) { toast.error("Inhalt erforderlich"); return; }

    setSending(true);
    try {
      const { data } = await api.post("/vorlagen/send", {
        template_id: draft?.template?.id, to, cc, bcc, subject, body,
        attachment_ids: attachmentIds,
        related_entity_type: relatedEntityType,
        related_entity_id: relatedEntityId,
      });
      setResult(data);
      if (data.ok) {
        setStage("success");
        toast.success("E-Mail versendet");
        onSent?.(data);
      } else {
        setStage("failed");
        toast.error(data.error || "Versand fehlgeschlagen");
      }
    } catch (e) {
      setStage("failed");
      setResult({ error: e.response?.data?.detail || "Fehler" });
    } finally {
      setSending(false);
    }
  }

  function csvOf(list, set) {
    return (
      <input
        value={list.join(", ")}
        onChange={(e) => set(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        className={inputCls + " text-xs font-mono"}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="send-email-dialog">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-display font-semibold text-zinc-100 inline-flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-400" /> {title}
            {draft?.template?.name && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
                Vorlage: {draft.template.name}
              </span>
            )}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
        </div>

        {loading && <div className="p-8 text-center text-zinc-500 text-sm">Lade Vorlage + Daten…</div>}

        {error && (
          <div className="p-5">
            <div className="px-3 py-3 bg-red-500/5 border border-red-500/30 rounded text-red-300 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Konnte Vorlage nicht laden</div>
                <div className="text-xs text-red-400 mt-0.5">{error}</div>
                <div className="text-[11px] text-zinc-500 mt-2">Tipp: Legen Sie unter <strong>Einstellungen → Vorlagen → E-Mail &amp; SMS</strong> eine Vorlage mit Auslöser „{eventTrigger}" an.</div>
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <button onClick={onClose} className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Schließen</button>
            </div>
          </div>
        )}

        {!loading && !error && draft && stage === "review" && (
          <>
            <div className="p-5 space-y-3 max-h-[65vh] overflow-y-auto">
              <div className="px-3 py-2 bg-amber-500/5 border border-amber-500/20 rounded text-amber-200 text-xs inline-flex items-start gap-2 w-full">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Bitte prüfen Sie die Empfänger und den Inhalt sorgfältig. <strong>Nichts wird gesendet</strong>, bevor Sie unten auf „Senden bestätigen" klicken.</span>
              </div>
              {draft.from_email && (
                <Field label="Absender">
                  <input readOnly value={draft.from_name ? `${draft.from_name} <${draft.from_email}>` : draft.from_email}
                    className={inputCls + " text-xs font-mono opacity-70"} />
                </Field>
              )}
              <Field label="An (kommagetrennt)" required>
                {csvOf(to, setTo)}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="CC">{csvOf(cc, setCc)}</Field>
                <Field label="BCC">{csvOf(bcc, setBcc)}</Field>
              </div>
              <Field label="Betreff" required>
                <input value={subject} onChange={(e) => setSubject(e.target.value)}
                  data-testid="send-subject"
                  className={inputCls} />
              </Field>
              <Field label="Inhalt (HTML)" required>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10}
                  data-testid="send-body"
                  className={inputCls + " font-mono text-xs"} />
              </Field>
              {(draft.attachments || []).length > 0 && (
                <Field label={`Anhänge (${draft.attachments.length})`}>
                  <div className="space-y-1">
                    {draft.attachments.map((a) => {
                      const checked = attachmentIds.includes(a.id);
                      return (
                        <label key={a.id} className="flex items-center gap-2 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded cursor-pointer hover:bg-zinc-900">
                          <input type="checkbox" checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) setAttachmentIds([...attachmentIds, a.id]);
                              else setAttachmentIds(attachmentIds.filter((x) => x !== a.id));
                            }}
                            className="w-3.5 h-3.5 accent-blue-600" />
                          <Paperclip className="w-3 h-3 text-zinc-500" />
                          <span className="text-xs text-zinc-200 flex-1">{a.name}</span>
                          <span className="text-[10px] text-zinc-500 font-mono">{((a.size_bytes || 0) / 1024).toFixed(1)} KB</span>
                        </label>
                      );
                    })}
                  </div>
                </Field>
              )}
            </div>
            <div className="px-5 py-3 border-t border-zinc-800 flex justify-between items-center gap-2">
              <button onClick={() => setStage("preview")} data-testid="send-preview-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">
                <Eye className="w-3.5 h-3.5" /> Vorschau
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Abbrechen</button>
                <button onClick={confirmSend} disabled={sending} data-testid="send-confirm-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {sending ? "Sende…" : "Senden bestätigen"}
                </button>
              </div>
            </div>
          </>
        )}

        {!loading && !error && stage === "preview" && (
          <>
            <div className="p-5 space-y-3 max-h-[65vh] overflow-y-auto">
              <div className="px-3 py-2 bg-blue-500/5 border border-blue-500/20 rounded text-blue-200 text-xs inline-flex items-start gap-2 w-full">
                <Eye className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Vorschau (gerendert) — so wird die E-Mail beim Empfänger ankommen.</span>
              </div>
              <div className="bg-zinc-950 border border-zinc-800 rounded p-4 space-y-3">
                <div className="text-[10px] uppercase text-zinc-500">An</div>
                <div className="text-sm text-zinc-100 font-mono">{to.join(", ")}</div>
                {cc.length > 0 && (<><div className="text-[10px] uppercase text-zinc-500">CC</div><div className="text-sm text-zinc-300 font-mono">{cc.join(", ")}</div></>)}
                <div className="text-[10px] uppercase text-zinc-500">Betreff</div>
                <div className="text-sm text-zinc-100">{subject}</div>
                <div className="text-[10px] uppercase text-zinc-500 mt-2">Inhalt</div>
                <div className="bg-zinc-900 border border-zinc-800 rounded p-3 prose prose-invert prose-sm max-w-none text-sm"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body || "") }} />
                {(draft.global_signature) && <div className="text-xs text-zinc-400 border-t border-zinc-800 pt-2">{draft.global_signature}</div>}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-800 flex justify-between items-center gap-2">
              <button onClick={() => setStage("review")}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">
                <Edit3 className="w-3.5 h-3.5" /> Zurück
              </button>
              <button onClick={confirmSend} disabled={sending} data-testid="send-confirm-btn-2"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded">
                <ShieldCheck className="w-3.5 h-3.5" />
                {sending ? "Sende…" : "Senden bestätigen"}
              </button>
            </div>
          </>
        )}

        {stage === "success" && (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h4 className="font-display font-semibold text-zinc-100 mb-1">E-Mail erfolgreich versendet</h4>
            <p className="text-xs text-zinc-500">Empfänger: {to.join(", ")}</p>
            <button onClick={onClose} className="mt-5 px-4 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Schließen</button>
          </div>
        )}
        {stage === "failed" && (
          <div className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h4 className="font-display font-semibold text-zinc-100 mb-1">Versand fehlgeschlagen</h4>
            <p className="text-xs text-red-300 max-w-md mx-auto">{result?.error || "Unbekannter Fehler"}</p>
            <p className="text-[11px] text-zinc-500 mt-2">Die E-Mail wurde nicht versendet. Versuchen Sie es erneut oder prüfen Sie die API-Konfiguration unter Einstellungen → APIs.</p>
            <div className="flex justify-center gap-2 mt-5">
              <button onClick={() => setStage("review")} className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Erneut versuchen</button>
              <button onClick={onClose} className="px-3 py-1.5 text-xs bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 rounded">Schließen</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}
